/**
 * The rights mark (1.8.0, docs/dev/rights-1.8.0-build.md): a small © coloured red or amber (contracts rights.ts
 * rightsColor) beside QcBadge on Library cards and rows, in the editor's bin and on timeline clips,
 * with a tooltip giving the status, dates and restriction (testid rights-mark). Nothing while rights management is
 * off (`rights` absent), or for a cleared or unknown state. RightsPill is the asset page's header pill.
 */
import { RIGHTS_STATE_LABELS, rightsColor, type RightsSummary } from '@prism/contracts';
import { rightsInstant, rightsMarked, rightsStateText, rightsTooltip } from '../lib/rights';

export function RightsMark(props: { rights: RightsSummary | null | undefined; size?: number }) {
  const r = props.rights;
  if (!rightsMarked(r)) return null;
  const tip = rightsTooltip(r);
  return (
    <span className={`rights-mark ${rightsColor(r)}`} data-testid="rights-mark" data-state={r.state} title={tip} role="img" aria-label={tip}
      style={props.size ? { fontSize: props.size } : undefined}>©</span>
  );
}

/** The asset page's header pill: the state in its colour; a click opens the Rights tab. Nothing while rights are off. */
export function RightsPill({ rights, onClick }: { rights: RightsSummary | null | undefined; onClick?: () => void }) {
  if (!rights) return null;
  return (
    <button type="button" className={`pill rights-pill ${rightsColor(rights)}`} data-testid="rights-pill" data-state={rights.state} title={rightsTooltip(rights)} onClick={onClick}>
      © {rightsStateText(rights)}
    </button>
  );
}

/** The Library inspector's banner for red states (withdrawn, ended, embargoed, not cleared): what it means and the restriction. */
export function RightsBanner({ rights }: { rights: RightsSummary | null | undefined }) {
  if (!rights || rightsColor(rights) !== 'red') return null;
  const what = rights.state === 'withdrawn' ? 'It must not be used. Exports, links and deliveries of it are blocked.'
    : rights.state === 'ended' ? `Its rights ended${rights.until ? ` on ${rights.until}` : ''}.`
    : rights.state === 'embargoed' ? `It is under embargo${rights.from ? ` until ${rightsInstant(rights.from)}` : ''}.`
    : 'Its rights are not cleared.';
  return (
    <div className="banner error rights-banner" role="status" data-testid="rights-banner" data-state={rights.state}>
      <div className="banner-body">
        <strong>© {RIGHTS_STATE_LABELS[rights.state]}</strong>
        <span>{what}{rights.fromSources ? ' This includes the sources it was made from.' : ''}</span>
        {rights.restrictions && <span className="muted">{rights.restrictions}</span>}
      </div>
    </div>
  );
}
